import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  DialogActions,
  Button,
  IconButton,
  DialogTitle,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { savePropertyDamage } from '../../../../store/incidentsReducer';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    background: '#2121214c',
  },
  '& .MuiPaper-root': {
    maxWidth: 800,
    width: 800,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    borderTop: 'none',
    scrollBehavior: 'smooth',
  },
  '& .MuiDialogActions-root': {
    '& .actionButton': {
      borderColor: '#212121',
      color: '#212121',

      '&.MuiButton-contained': {
        background: '#212121',
        color: 'white',
        '&:disabled': {
          background: 'gray',
        },
      },
      '&.success': {
        '&.MuiButton-contained': {
          background: '#75ec9f',
          color: '#000000dd',
        },
      },
    },
  },
}));

export default function PropertyDamageModal({ open, setOpen }) {
  const [propertiesList, setPropertiesList] = useState([]);
  const [fetchProperties, setFetchProperties] = useState(
    !!open?.isHasProperty
  );
  const defaultRows = useMemo(
    () => [{ id: null, amount: '', description: '' }],
    []
  );
  const dispatch = useDispatch();

  const { control, register, handleSubmit } = useForm({
    defaultValues: { properties: defaultRows },
  });
  const watchedProperties = useWatch({ control, name: 'properties' });

  const { fields, append, replace, remove } = useFieldArray({
    control,
    name: 'properties',
  });

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setFetchProperties(true);
        const response = await incidentsApi.getPropertyDamage(open.id);

        if (response.status === 200) {
          setPropertiesList(response.data || []);
        } else throw new Error();
      } catch (error) {
        toast.error('Error getting property damage');
      } finally {
        setFetchProperties(false);
      }
    };

    if (!!open && open.isHasProperty && propertiesList.length === 0) {
      fetchNote();
    } else if (!open && propertiesList.length > 0) {
      setPropertiesList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (Array.isArray(propertiesList) && propertiesList.length > 0) {
      replace(
        propertiesList.map((item) => ({
          id: item?.id ?? null,
          amount: item?.amount ?? '',
          description: item?.description ?? '',
        }))
      );
    } else {
      replace(defaultRows);
    }
  }, [propertiesList, replace, defaultRows]);

  const closeHandler = () => {
    setPropertiesList([]);
    setOpen(false);
  };

  const handleSaveProperty = async (values) => {
    let payload = { properties: [] };
    if (values.properties.length > 0) {
      payload.properties = values.properties.map((item) => {
        const amountValue =
          item.amount === '' ||
          item.amount === null ||
          item.amount === undefined
            ? ''
            : Number(item.amount);
        return {
          id: item.id ?? undefined,
          amount: Number.isFinite(amountValue) ? amountValue : item.amount,
          description: item.description,
          additional_field_1: '',
        };
      });
    }
    dispatch(savePropertyDamage(open.id, payload)).then((resp) => {
      if (resp) closeHandler();
    });
  };

  return (
    <BootstrapDialog
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          closeHandler();
        }
      }}
      aria-labelledby="view-incident-modal"
      open={!!open}
    >
      <DialogTitle>
        <Typography
          sx={{ fontSize: '22px', fontWeight: 500, lineHeight: '25px' }}
        >
          Property Damage
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeHandler}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#212121',
            zIndex: '999',
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {fetchProperties ? (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fields.map((field, index) => (
              <Box
                key={field.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <TextField
                  {...register(`properties.${index}.amount`)}
                  label="$ Amount"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ flexBasis: '25%', flexGrow: 0, flexShrink: 0 }}
                />
                <TextField
                  {...register(`properties.${index}.description`)}
                  label="Description"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ flexGrow: 1 }}
                />
                <IconButton
                  aria-label="remove property damage row"
                  onClick={() => remove(index)}
                  size="small"
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                aria-label="add property damage row"
                onClick={() =>
                  append({ id: null, amount: '', description: '' })
                }
                sx={{ border: '1px solid #E1E1E1' }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          className="actionButton"
          onClick={() => setOpen(null)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="actionButton ms-2"
          onClick={handleSubmit(handleSaveProperty)}
        >
          Save
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
